/**
 * Adapter functions that transform raw backend responses (backendTypes.ts)
 * into the frontend types (types.ts) consumed by components, and frontend
 * payloads back into the wire shape the backend expects.
 */

import type {
  BackendGprtPage,
  BackendGprtRequestSummary,
  BackendGprtRequestDetail,
  BackendGprtRequestedItinerary,
  BackendGprtQuotedItinerary,
  BackendGprtAttachment,
  BackendCreateGprtRequestBody,
  BackendMeetingRoomConfig,
  BackendMeetingRoomConfigOption,
} from "./backendTypes";
import type {
  GprtTabStatus,
  GprtRoomStatus,
  FileStatus,
  GprtRequestSummary,
  GprtRequestListResult,
  GprtRequestDetail,
  GprtRequestedItinerary,
  GprtQuotedItinerary,
  GprtAttachment,
  CreateGprtRequestPayload,
  ListGprtRequestsParams,
  MeetingRoomConfig,
  MeetingRoomConfigOption,
} from "./types";

const toNull = <T>(val: T | undefined | null): T | null =>
  val === undefined || val === null ? null : val;

const toTabStatus = (raw: string): GprtTabStatus => raw as GprtTabStatus;

const toRoomStatus = (raw: string): GprtRoomStatus => raw as GprtRoomStatus;

/** Placeholder platformRfpId for the "Other" platform, which has no external
 * RFP system to reference an ID from — mirrors GPC's generatePlatformRfpId
 * (adapters.ts) but with a GPRT-specific prefix so the two aren't confusable. */
export const generatePlatformRfpId = (): string => {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 6);
  return `GPRT-${ts}-${rand}`.toUpperCase();
};

const toFileStatus = (raw: string | undefined): FileStatus | null =>
  raw ? (raw as FileStatus) : null;

const humanizeCommentSectionKey = (key: string): string =>
  key
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// Shared by formatRequestedComments/parseSectionedComments: both fields are
// either legacy/plain free text or a JSON object keyed by section (e.g.
// {"room": "...", "meeting_room": "..."}) — this extracts just the non-empty
// string-valued entries, or null if `raw` isn't that shape at all.
const parseNonEmptyStringSections = (
  raw: string | null
): [string, string][] | null => {
  if (raw == null) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return Object.entries(parsed).filter(
        (entry): entry is [string, string] =>
          typeof entry[1] === "string" && entry[1].length > 0
      );
    }
  } catch {
    // Not JSON — legacy/plain free text, fall through.
  }
  return null;
};

// Backend now sends requestedComments as a raw passthrough (no more server-side
// pre-formatting) — it's either legacy free text, or a GPRT-originated JSON blob
// keyed by section, e.g. {"room": "...", "meeting_room": "..."}. Reproduce the
// old server-side formatting here so the drawer's display doesn't change —
// generically over every string-valued key, not just "room"/"meeting_room",
// so any other section the backend has ever written isn't silently dropped.
const formatRequestedComments = (raw: string | null): string | null => {
  const sections = parseNonEmptyStringSections(raw);
  // null sections means `raw` wasn't the JSON-object shape at all (not JSON,
  // or an array) — fall back to it as plain text. An empty (non-null) list
  // means it parsed as a JSON object but had no matching values, which also
  // isn't the room/meeting_room shape — same fallback.
  if (!sections || sections.length === 0) return raw;
  if (sections.length === 1) return sections[0][1];
  return sections
    .map(([key, value]) => `${humanizeCommentSectionKey(key)}: ${value}`)
    .join("\n");
};

// `quoted_comments` (added 2026-08-17) is always JSON-encoded, keyed by
// section ("room"/"meeting_room") — GprtRequestService.mergeQuotedComment is
// the only writer, for both accepted and declined decisions. Kept as a
// per-key record (not flattened to one string like formatRequestedComments)
// so the drawer can show each side's own decision comment next to that
// side's own accept/decline indicator.
const parseSectionedComments = (
  raw: string | null
): Record<string, string> | null => {
  const sections = parseNonEmptyStringSections(raw);
  return sections && sections.length > 0 ? Object.fromEntries(sections) : null;
};

// ══════════════════════════════════════════════════
// List Requests
// ══════════════════════════════════════════════════

export const adaptGprtSummary = (
  raw: BackendGprtRequestSummary
): GprtRequestSummary => ({
  rfpId: raw.rfp_id,
  propertyId: raw.property_id,
  platformRfpId: toNull(raw.platform_rfp_id),
  propertyName: raw.property_name,
  platformId: raw.platform_id,
  platformName: raw.platform_name,
  hotelRoomRequested: raw.hotel_room_requested,
  meetingSpaceRequested: raw.meeting_space_requested,
  reviewStatus: toTabStatus(raw.review_status),
  requestedStartDate: raw.requested_start_date,
  requestedEndDate: raw.requested_end_date,
  requestedTotalRooms: toNull(raw.requested_total_rooms),
  quotedBookingValue: toNull(raw.quoted_booking_value),
  createdAt: raw.created_at,
});

// Pagination on this endpoint is broken server-side (totalElements/totalPages/last
// don't reflect the true dataset size) — `hasMore` is derived from whether the page
// came back full, per the contract's documented workaround.
export const adaptGprtListResponse = (
  raw: BackendGprtPage<BackendGprtRequestSummary> | undefined,
  requestedSize: number
): GprtRequestListResult => {
  const content = (raw?.content ?? []).map(adaptGprtSummary);
  return { content, hasMore: content.length === requestedSize };
};

// ══════════════════════════════════════════════════
// Request Detail
// ══════════════════════════════════════════════════

const adaptRequestedItinerary = (
  raw: BackendGprtRequestedItinerary
): GprtRequestedItinerary => ({
  checkinDate: raw.checkin_date,
  checkoutDate: raw.checkout_date,
  peakRoomCount: toNull(raw.peak_room_count),
  totalRoomCount: toNull(raw.total_room_count),
  roomTypeText: toNull(raw.room_type_text),
  budgetMin: toNull(raw.budget_min),
  budgetMax: toNull(raw.budget_max),
  compType: toNull(raw.comp_type),
  compValue: toNull(raw.comp_value),
  metadata: toNull(raw.metadata),
});

const adaptQuotedItinerary = (
  raw: BackendGprtQuotedItinerary
): GprtQuotedItinerary => ({
  checkinDate: raw.checkin_date,
  checkoutDate: raw.checkout_date,
  roomTypeText: toNull(raw.room_type_text),
  price: toNull(raw.price),
  roomCount: toNull(raw.room_count),
  metadata: toNull(raw.metadata),
});

// Exported for reuse by GPC's ActionApis — GPC's drawer shows this same
// attachment shape for GPRT-sourced requests' merged "GPRT context" (see
// PricingRequestDetailWithGprtContext in grouppricingcalculator/ActionApis).
export const adaptAttachment = (
  raw: BackendGprtAttachment
): GprtAttachment => ({
  name: toNull(raw.name),
  type: toNull(raw.type),
  url: toNull(raw.url),
  status: toFileStatus(raw.status),
  bytes: toNull(raw.bytes),
});

export const adaptGprtDetail = (
  raw: BackendGprtRequestDetail
): GprtRequestDetail => ({
  rfpId: raw.rfp_id,
  propertyId: raw.property_id,
  platformId: raw.platform_id,
  platformName: raw.platform_name,
  platformRfpId: toNull(raw.platform_rfp_id),
  rfpLink: toNull(raw.rfp_link),
  requestedComments: formatRequestedComments(toNull(raw.requested_comments)),
  requestedStartDate: raw.requested_start_date,
  requestedEndDate: raw.requested_end_date,
  requestedTotalRooms: toNull(raw.requested_total_rooms),
  requestedCommissionValue: toNull(raw.requested_commission_value),
  requestedRebateValue: toNull(raw.requested_rebate_value),
  hotelRoomStatus: toRoomStatus(raw.hotel_room_status),
  meetingRoomStatus: toRoomStatus(raw.meeting_room_status),
  reviewStatus: toTabStatus(raw.review_status),
  createdAt: raw.created_at,
  requestedItineraries: (raw.requested_itineraries ?? []).map(
    adaptRequestedItinerary
  ),
  quotedItineraries: (raw.quoted_itineraries ?? []).map(adaptQuotedItinerary),
  quotedComments: parseSectionedComments(toNull(raw.quoted_comments)),
  attachments: (raw.attachments ?? []).map(adaptAttachment),
});

// ══════════════════════════════════════════════════
// Create Payload
// ══════════════════════════════════════════════════

export const adaptCreateGprtPayload = (
  payload: CreateGprtRequestPayload,
  propertyId: string
): BackendCreateGprtRequestBody => {
  const omitCommission =
    !payload.commissionType || payload.commissionType === "NONE";
  const omitRebate = !payload.rebateType || payload.rebateType === "NONE";
  return {
    platformRfpId: payload.platformRfpId,
    requestSource: payload.requestSource,
    propertyId,
    platformId: payload.platformId,
    requestedStartDate: payload.requestedStartDate,
    requestedEndDate: payload.requestedEndDate,
    commissionType: omitCommission ? undefined : payload.commissionType,
    requestedCommissionValue: omitCommission
      ? undefined
      : payload.requestedCommissionValue,
    rebateType: omitRebate ? undefined : payload.rebateType,
    requestedRebateValue: omitRebate ? undefined : payload.requestedRebateValue,
    salesforceUrl: payload.salesforceUrl || undefined,
    // Snake_case exception #1 — see contract's "Casing gotcha" callout.
    requested_comments: payload.requestedComments || undefined,
    itineraries: payload.itineraries?.length ? payload.itineraries : undefined,
    meetingRooms: payload.meetingRooms?.length
      ? payload.meetingRooms
      : undefined,
    // Snake_case exception #2.
    attachment_ids: payload.attachmentIds?.length
      ? payload.attachmentIds
      : undefined,
  };
};

// ══════════════════════════════════════════════════
// Meeting Room Config
// ══════════════════════════════════════════════════

const adaptMeetingRoomConfigOption = (
  raw: BackendMeetingRoomConfigOption
): MeetingRoomConfigOption => ({
  id: raw.id,
  label: raw.label,
});

export const adaptMeetingRoomConfig = (
  raw: BackendMeetingRoomConfig
): MeetingRoomConfig => ({
  amenities: (raw.amenities ?? []).map(adaptMeetingRoomConfigOption),
  roomSetups: (raw.roomSetups ?? []).map(adaptMeetingRoomConfigOption),
});

// ══════════════════════════════════════════════════
// List Query Params
// ══════════════════════════════════════════════════

export const adaptGprtListParams = (
  params?: ListGprtRequestsParams
): Record<string, string | number | undefined> | undefined => {
  if (!params) return undefined;
  return {
    status: params.status,
    page: params.page,
    size: params.size,
    sort: params.sort,
  };
};
